// --- Core ---
import {FC, ReactElement, useEffect, useState} from "react";
import {FieldRenderProps} from "react-final-form";
import {findWhere, map} from "underscore";
// --- Material UI ---
import {FormControl, FormHelperText, InputLabel, MenuItem, Select, SelectProps} from "@mui/material";
// --- SaniQ ---
import {Country} from "../../../models/Country";
import {useAppDispatch, useAppSelector} from "store";
import {fetchCountries} from "src/modules/countries/actions/Countries";

interface CountryStateSelectFieldProps extends FieldRenderProps<string> {
  label: ReactElement;
  required?: boolean;
  // The code of the selected country
  selectedCountryCode: string;
  SelectDisplayProps: SelectProps["SelectDisplayProps"];
}

/**
 * A select field for choosing a country's state
 */
export const CountryStateSelectField: FC<CountryStateSelectFieldProps> = ({
  selectedCountryCode,
  input,
  label,
  required,
  meta: {touched, invalid, error, submitError},
  SelectDisplayProps,
  ...custom
}) => {
  const [countryStates, setCountryStates] = useState<Country["subdivisions"]>([]);
  const [isDisabled, setIsDisabled] = useState(false);
  const countries = useAppSelector(state => state.countries.collection);
  const isLoading = useAppSelector(state => state.countries.isLoading);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (countries.length === 0 && !isLoading) {
      dispatch(fetchCountries());
    }
  }, [countries, dispatch, isLoading]);

  useEffect(() => {
    setCountryStates([]);
    setIsDisabled(false);
    const country = findWhere(countries, {id: selectedCountryCode});

    if (country?.subdivisions?.length) {
      setCountryStates(country.subdivisions);
    } else {
      setIsDisabled(true);
    }
  }, [countries, selectedCountryCode]);

  return (
    <FormControl
      fullWidth variant="outlined"
      error={touched && invalid}
    >
      <InputLabel
        variant="outlined"
        id={`${input.name}_label`}
        required={required}
      >
        {label}
      </InputLabel>
      <Select
        {...input}
        labelId={`${input.name}_label`}
        label={label}
        disabled={isDisabled}
        error={touched && invalid}
        SelectDisplayProps={SelectDisplayProps}
        {...custom}
      >
        {
          map(countryStates, (state) => (
            <MenuItem value={state.code}>
              {state.name}
            </MenuItem>
          ))
        }
      </Select>
      {
        touched && (error || submitError) && (
          <FormHelperText>
            {error || submitError.join(", ")}
          </FormHelperText>
        )
      }
    </FormControl>
  );
};
